'use client';

import * as React from 'react';
import { ArrowRight, Activity, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useExternalRequestsList } from '@/lib/api/external-requests';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { cn } from '@/lib/utils';

interface IntegrationS2SLogsProps {
  serviceName: string; // 'meta', 'klaviyo', 'webhook', etc.
  storeId: string;
  limit?: number;
}

/**
 * Component to display recent S2S event logs for a specific integration
 * Shows in the integration details page with link to full logs
 */
export function IntegrationS2SLogs({ serviceName, storeId, limit = 5 }: IntegrationS2SLogsProps) {
  const { orgRoutes, navigate } = useOrgNavigate();

  // Fetch recent logs for this service and store
  const { data: logsResponse, isLoading } = useExternalRequestsList(
    {
      pagination: { page: 0, pageSize: limit },
      sortBy: { field: 'createdAt', direction: 'desc' },
      filters: {
        'externalRequestLogs.service': [serviceName.toLowerCase()],
        'externalRequestLogs.storeId': [storeId],
      },
    },
    false,
  );

  const logs = logsResponse?.logs || [];
  const total = logsResponse?.total || 0;

  const handleViewAll = () => {
    // Navigate to S2S logs with pre-filtered service and store
    navigate(
      `${orgRoutes.EXTERNAL_REQUESTS()}?service=${serviceName.toLowerCase()}&store_id=${storeId}`,
    );
  };

  const handleLogClick = (logId: string) => {
    navigate(orgRoutes.EXTERNAL_REQUEST_DETAILS(logId));
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent S2S Events</CardTitle>
          <CardDescription>Loading recent server-to-server events...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Recent S2S Events</CardTitle>
            <CardDescription>
              {total > 0 ? `${total.toLocaleString()} total events` : 'No events yet'}
            </CardDescription>
          </div>
          {total > 0 && (
            <Button variant="outline" size="sm" onClick={handleViewAll}>
              View All
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Activity className="text-muted-foreground mb-3 h-12 w-12" />
            <p className="text-sm font-medium">No S2S events yet</p>
            <p className="text-muted-foreground mt-1 text-xs">
              Events will appear here when this integration sends data
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log: any) => (
              <button
                key={log.id}
                onClick={() => handleLogClick(log.id)}
                className="bg-card hover:bg-accent w-full rounded-lg border p-3 text-left transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      {log.status === 'success' ? (
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 flex-shrink-0 text-red-600" />
                      )}
                      <span className="truncate font-mono text-xs font-medium">{log.event_type}</span>
                      {log.event_name && (
                        <Badge variant="outline" className="text-xs">
                          {log.event_name}
                        </Badge>
                      )}
                    </div>
                    {log.error_message && (
                      <p className="text-destructive truncate text-xs">{log.error_message}</p>
                    )}
                    <div className="text-muted-foreground flex items-center gap-2 text-xs">
                      <span>{format(new Date(log.created_at), 'MMM dd, HH:mm:ss')}</span>
                      {log.duration_ms && (
                        <span className="font-mono">
                          {log.duration_ms < 1000
                            ? `${log.duration_ms}ms`
                            : `${(log.duration_ms / 1000).toFixed(2)}s`}
                        </span>
                      )}
                      {log.response_status && (
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs',
                            log.response_status >= 200 && log.response_status < 300
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : 'border-red-200 bg-red-50 text-red-700',
                          )}
                        >
                          {log.response_status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ExternalLink className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

